// app/ has no slice, so no lib/ to hold pure functions — colocate the handler next
// to the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import { handleLoginAsClinic, CONFIG } from '@/entities/user';
import initSession from '@/app/lib/init-session';
import { EMITTER_CONSTANTS, ERROR_ACTION } from '@/shared/config';
import { auth, cookies, emitter, SocketIo } from '@/shared/lib';
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { Amplify, Auth } from 'aws-amplify';
import _ from 'lodash';

/**
 * Wipes every browser cookie for the portal's domain as part of tearing a session down. Delegates to the shared cookies gateway, which expires each visible cookie on path '/' under the local or environment cookie domain.
 *
 * @returns Nothing.
 */
const clearCookies = () => {
  cookies.clearAll();
};

/**
 * Starts the ERP token handshake once the hidden auth iframe has loaded. Looks up the element with id 'iframe-id' and posts a get message for the 'shareData' key into its content window.
 *
 * @returns Nothing.
 */
const onLoadIframe = () => {
  const iframe = document.getElementById('iframe-id') as any;
  if (iframe) {
    iframe.contentWindow.postMessage(
      {
        action: 'get',
        key: 'shareData',
      },
      '*',
    );
  }
};

/**
 * Renews an expired Cognito session from the refresh token held on it. Wraps authUser.refreshSession in a promise that always resolves, yielding isSuccess false plus the error message on failure and isSuccess true otherwise.
 *
 * @param authUser - Cognito user whose session is refreshed.
 * @param session - Cognito session supplying the refresh token.
 * @returns A promise resolving to an isSuccess flag and, on failure, the error message.
 */
const refreshSession = (authUser: any, session: any) =>
  new Promise<any>((resolve) => {
    authUser.refreshSession(session.refreshToken, (error: any) => {
      if (error) {
        resolve({
          isSuccess: false,
          error: error.message,
        });
      } else {
        resolve({
          isSuccess: true,
        });
      }
    });
  });

/**
 * Checks that a reconstructed Cognito session is still usable and refreshes it when the id token has expired. Compares the id-token expiry against the current epoch seconds and delegates to the refresh-token flow when it has passed, otherwise awaits Auth.currentSession, returning isSuccess false with the error message when anything throws.
 *
 * @param authUser - Cognito user owning the session.
 * @param session - Cognito session whose id token is inspected.
 * @returns A promise resolving to an isSuccess flag and, on failure, the error message.
 */
const validateSession = async (authUser: any, session: any) => {
  try {
    const idTokenExpire = session.getIdToken().getExpiration();
    const currentTimeSeconds = Math.round(+new Date() / 1000);
    if (idTokenExpire <= currentTimeSeconds) {
      const refreshResult = await refreshSession(authUser, session);
      return refreshResult;
    }
    await Auth.currentSession();
    return {
      isSuccess: true,
    };
  } catch (error) {
    return {
      isSuccess: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

/**
 * Rebuilds a local Cognito session from the tokens the ERP portal shared over postMessage. Assembles a CognitoUserSession from the id, access and refresh tokens, attaches it to a Cognito user created for shareData.email, and returns the validation result which refreshes the tokens when the id token has already expired.
 *
 * @param shareData - Tokens and email shared by the ERP portal.
 * @returns A promise resolving to the session validation result carrying isSuccess.
 */
const createNewCognitoUser = async (shareData: any) => {
  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: shareData.idToken }),
    AccessToken: new CognitoAccessToken({
      AccessToken: shareData.accessToken,
    }),
    RefreshToken: new CognitoRefreshToken({
      RefreshToken: shareData.refreshToken,
    }),
  });
  const authUser = (Auth as any).createCognitoUser(shareData.email);
  authUser.setSignInUserSession(session);
  const result = await validateSession(authUser, session);
  return result;
};

/**
 * Empties localStorage while preserving the stored 'pathname' entry that drives the post-login redirect. Reads pathname first, clears the whole store, then writes it back when it was present.
 *
 * @returns Nothing.
 */
const handleClearLocalStorage = () => {
  auth.logoutExceptPathname();
};

/**
 * Tears down the Cognito session established through the Microsoft/ERP login path. Always expires the domain cookies, and for a Microsoft-account login also clears localStorage except 'pathname' before awaiting Auth.signOut.
 *
 * @returns A promise resolving once the sign-out work has finished.
 */
const handleSignOutCurrentCognitoUser = async () => {
  clearCookies();
  if (auth.isLoginWithMicrosoftAccount()) {
    handleClearLocalStorage();
    await Auth.signOut();
  }
};

/**
 * Confirms that the Cognito session Amplify currently holds belongs to the given email. Reads the authenticated user's email attribute and returns false when it differs, or when the lookup throws, in which case the failure is logged to the console.
 *
 * @param email - Email address the current Cognito user is expected to have.
 * @returns A promise resolving to true when the authenticated Cognito user's email matches, false otherwise.
 */
const handleCheckCurrentCognitoUser = async (email: any) => {
  try {
    // check latest user data
    const currentCognitoUser = await Auth.currentAuthenticatedUser();
    const currentEmail = currentCognitoUser?.attributes?.email;
    if (currentEmail !== email) {
      return false;
    }
    return true;
  } catch (error) {
    console.error('Failed to check current Cognito user: ', error);
    return false;
  }
};

/**
 * Processes the postMessage traffic from the ERP iframe to establish or drop the portal session. On 'returnData' it rebuilds the Cognito session from the shared tokens when they belong to another email, persists them, applies the login-through-admin/sales flags and the clinic authorization token, then connects the socket and initialises the session; 'signOutPortal' signs out and navigates to the given URL, and any failure emits a logout request.
 *
 * @param event - Message event whose data carries the action and value.
 * @returns A promise resolving once the message has been handled.
 */
const messageHandler = async (event: any) => {
  const { action, value } = event.data;
  switch (action) {
    case 'returnData': {
      try {
        const { shareData } = value;

        if (_.isNil(shareData?.accessToken)) {
          await handleSignOutCurrentCognitoUser();
          emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.REDIRECT);
          return;
        }

        const isValidCurrentUser = await handleCheckCurrentCognitoUser(shareData?.email);

        if (!isValidCurrentUser) {
          await handleSignOutCurrentCognitoUser();
          const result = await createNewCognitoUser(shareData);
          if (!result?.isSuccess) {
            emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.REDIRECT);
            return;
          }
        }

        const { accessToken, idToken, refreshToken, email } = shareData;
        auth.saveShareData({
          accessToken,
          idToken,
          refreshToken,
          email,
          isLoginWithMicrosoftAccount: true,
        });

        const isLoginThroughAdmin = CONFIG.IS_LOGIN_THROUGH_ADMIN;
        const isLoginThroughSales = CONFIG.IS_LOGIN_THROUGH_SALES;
        if (isLoginThroughAdmin || isLoginThroughSales) {
          if (isLoginThroughAdmin) {
            auth.setIsLoginThroughAdmin(isLoginThroughAdmin);
            auth.setIsLoginThroughSales(false);
            auth.setIsShowLoginByBanner(true);
          } else {
            auth.setIsLoginThroughSales(isLoginThroughSales);
            auth.setIsLoginThroughAdmin(false);
            auth.setIsShowLoginByBanner(true);
          }
          CONFIG.IS_LOGIN_THROUGH_ADMIN = undefined;
          CONFIG.IS_LOGIN_THROUGH_SALES = undefined;
          if (CONFIG.ADMIN_OR_SALES_USER_ID && CONFIG.ADMIN_OR_SALES_FACILITY_ID) {
            const userId = CONFIG.ADMIN_OR_SALES_USER_ID as string;
            const facilityId = CONFIG.ADMIN_OR_SALES_FACILITY_ID as string;
            auth.setUserId(userId);
            auth.setAdminOrSalesFacilityId(facilityId);
            const authorizedToken = await handleLoginAsClinic({
              isLoginThroughAdmin: auth.isLoginThroughAdmin(),
              userId,
              facilityId,
            });
            auth.setAuthorizedToken(authorizedToken);
            auth.setUserFacilityId(facilityId);
            CONFIG.ADMIN_OR_SALES_FACILITY_ID = undefined;
          }
        }

        (SocketIo as any).connectToServer();
        initSession();
      } catch (error) {
        console.error('Failed to create Cognito user: ', error);
        emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.REDIRECT);
      }
      break;
    }
    case 'signOutPortal': {
      if (value?.urlRedirect) {
        await handleSignOutCurrentCognitoUser();
        document.location.href = value?.urlRedirect;
      } else {
        emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.REDIRECT);
      }
      break;
    }
    default: {
      break;
    }
  }
};

/**
 * Removes the injected ERP cross-storage message script from the document. Scans the script tags backwards for the one recorded as document.idMessage, detaches it from its parent, clears the marker and reloads the page.
 *
 * @returns Nothing.
 */
const removeScriptMessage = () => {
  const tags = document.getElementsByTagName('script');
  for (let i = tags.length; i >= 0; i -= 1) {
    if (tags[i] === (document as any).idMessage) {
      tags[i].parentElement?.removeChild(tags[i]);
      (document as any).idMessage = undefined;
      window.location.reload();
    }
  }
};

/**
 * Repoints Amplify's hosted-UI sign-out redirect at a partner portal such as CardioOne. Reads the current Amplify configuration back and re-applies it with oauth.redirectSignOut replaced by the given URL.
 *
 * @param newUrl - URL Cognito redirects to after sign-out.
 * @returns Nothing.
 */
const updateSignOutRedirect = (newUrl: any) => {
  const currentConfig = Amplify.configure() as any;
  Amplify.configure({
    ...currentConfig,
    oauth: {
      ...currentConfig.oauth,
      redirectSignOut: newUrl,
    },
  });
};

export { clearCookies, messageHandler, onLoadIframe, removeScriptMessage, updateSignOutRedirect };
